<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import CoreField from '@/components/core/form/CoreField.vue'
import CoreSelectField from '@/components/core/form/fields/CoreSelectField.vue'
import type { CoreSelectItem } from '@/components/core/form/types'
import { STORY_FONTS, isStoryFontId } from '@/features/story-conversation/storyFonts'
import { themes } from '@/lib/themes'
import { useAppearanceDraft } from '@/features/appearance/useAppearanceDraft'
import { resolveAppearance } from '@/features/appearance/resolveAppearance'
import { normalizeStoryColor } from '@/features/appearance/preferences'
import type { StoryTextKind } from '@/types/preferences'
import { parseMarkdown } from '@/utils/markdown'
import '@/components/chat/storyProse.css'

const { store, draft, error, dirty, cancel, save } = useAppearanceDraft()
const resolved = computed(() => resolveAppearance(draft.value))
const colors = computed(() => resolved.value.colors)
const overrides = computed(() => resolved.value.overrides)
const appearanceStyles = computed(() => ({ ...resolved.value.theme.vars, ...resolved.value.appearanceStyles,
  background: resolved.value.theme.preview.bg, color: colors.value.normal }))
const fontOptions = Object.entries(STORY_FONTS).map(([id, option]) => ({ id, ...option }))
const themeOptions = Object.values(themes).map(theme => ({ ...theme }))
const selectedFont = computed(() => fontOptions.find(option => option.id === draft.value.story.fontId)!)
const selectedTheme = computed(() => themeOptions.find(theme => theme.id === draft.value.app.themeId)!)
const themeLabel = computed(() => selectedTheme.value.label)
function selectFont(option: CoreSelectItem | null): void {
  if (option && typeof option === 'object' && isStoryFontId(option.id)) draft.value.story.fontId = option.id
}
function selectTheme(option: CoreSelectItem | null): void {
  if (option && typeof option === 'object' && typeof option.id === 'string' && Object.hasOwn(themes, option.id)) draft.value.app.themeId = option.id
}
function setColor(kind: StoryTextKind, value: string): void {
  const color = normalizeStoryColor(value)
  if (!color) return
  const id = draft.value.app.themeId
  draft.value.story.themes[id] = { colors: { ...overrides.value, [kind]: color } }
}
function resetColor(kind: StoryTextKind): void {
  const values = { ...overrides.value }
  delete values[kind]
  draft.value.story.themes[draft.value.app.themeId] = { colors: values }
}
function resetColors(): void { delete draft.value.story.themes[draft.value.app.themeId] }
const valid = computed(() => Object.values(drafts.value).every(value => normalizeStoryColor(value)))
const fields: { kind: StoryTextKind; label: string; description: string }[] = [
  { kind: 'normal', label: 'Normal text', description: 'Narration and ordinary text' },
  { kind: 'dialogue', label: 'Dialogue', description: 'Speech in double quotation marks' },
  { kind: 'italic', label: 'Italicized text', description: 'Italics and blockquotes' },
  { kind: 'monospace', label: 'Monospace', description: 'Inline code and code blocks' },
]
const drafts = ref({ ...colors.value })
watch(colors, values => { drafts.value = { ...values } })

function updateHex(kind: StoryTextKind, event: Event, commit = false): void {
  const value = (event.target as HTMLInputElement).value
  drafts.value[kind] = value
  // Let people finish typing six digits before expanding a three-digit shorthand.
  if (commit || /^#?[\da-f]{6}$/i.test(value)) setColor(kind, value)
}

const preview = parseMarkdown(
  'The lantern cast a **warm glow**.\n\n“Stay close,” she whispered. *Footsteps echoed beyond the door.*\n\n> Something was waiting.\n\nThe sign read `ENTER QUIETLY`.',
  {}, { dialogue: true },
)
</script>

<template>
  <section class="conversation-appearance" aria-label="Conversation appearance">
    <p class="conversation-appearance__scope">
      Saved to your account. Theme applies throughout the app; font and colors apply to roleplay and Open Worlds.
      Colors are saved separately for each theme. Editing {{ themeLabel }}.
    </p>

    <p v-if="store.loading" role="status">Loading appearance…</p>
    <p v-if="store.error" role="alert">Could not load appearance: {{ store.error }}
      <button type="button" @click="store.load(store.userId)">Retry</button>
    </p>
    <fieldset :disabled="!store.loaded || store.saving" class="conversation-appearance__fields">
      <CoreSelectField name="app-theme" label="App theme" required :model-value="selectedTheme"
        :items="themeOptions" item-title="label" @update:model-value="selectTheme" />
      <CoreSelectField
        name="story-font"
        label="Font"
        required
        description="Shared across themes."
        :model-value="selectedFont"
        :items="fontOptions"
        item-title="label"
        @update:model-value="selectFont"
      />
      <CoreField
        v-for="field in fields"
        :key="field.kind"
        :name="`story-color-${field.kind}`"
        :label="field.label"
        :description="field.description"
        :error-messages="normalizeStoryColor(drafts[field.kind]) ? [] : ['Use a hex color, such as #aabbcc.']"
      >
        <template #default="{ id, describedBy, invalid }">
          <div class="conversation-appearance__controls">
            <input
              type="color"
              :aria-label="`${field.label} color picker`"
              :value="colors[field.kind]"
              @input="setColor(field.kind, ($event.target as HTMLInputElement).value)"
            />
            <input
              :id="id"
              type="text"
              :aria-label="`${field.label} hex color`"
              :aria-describedby="describedBy"
              :aria-invalid="invalid"
              :value="drafts[field.kind]"
              maxlength="7"
              spellcheck="false"
              autocomplete="off"
              autocapitalize="off"
              @input="updateHex(field.kind, $event)"
              @change="updateHex(field.kind, $event, true)"
            />
            <button
              type="button"
              class="conversation-appearance__reset"
              :aria-label="`Reset ${field.label.toLowerCase()} color`"
              :disabled="!overrides[field.kind] && normalizeStoryColor(drafts[field.kind]) !== null"
              @click="resetColor(field.kind)"
            >Reset</button>
          </div>
        </template>
      </CoreField>
    </fieldset>

    <div class="conversation-appearance__preview" :style="appearanceStyles">
      <span class="conv-menu__eyebrow">Preview</span>
      <div class="story-prose" v-html="preview" />
    </div>

    <button
      type="button"
      class="conversation-appearance__reset conversation-appearance__reset-all"
      :disabled="!store.loaded || store.saving || !Object.keys(overrides).length"
      @click="resetColors"
    >Reset all colors to theme defaults</button>
    <p v-if="error" role="alert">{{ error }}</p>
    <div class="appearance-actions">
      <BaseButton :disabled="!dirty || !valid || !store.loaded || store.saving" @click="save">{{ store.saving ? 'Saving…' : 'Save' }}</BaseButton>
      <BaseButton variant="secondary" :disabled="store.saving" @click="cancel">Cancel</BaseButton>
    </div>
  </section>
</template>

<style scoped>
.conversation-appearance {
  padding: 0 18px 18px;
  color: var(--theme-on-surface);
}

.conversation-appearance__scope {
  margin: 0 0 18px;
  color: var(--theme-on-surface-variant);
  font-size: 0.78rem;
  line-height: 1.5;
}

.conversation-appearance__fields {
  display: grid;
  gap: 14px;
  border: 0;
  padding: 0;
  margin: 0;
  min-width: 0;
}

.conversation-appearance__controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.conversation-appearance__controls input {
  height: 36px;
  border: 1px solid var(--theme-border-hover);
  border-radius: 6px;
  background: var(--theme-surface-solid);
  color: var(--theme-on-surface);
}

.conversation-appearance__controls input[type='color'] {
  width: 40px;
  flex-shrink: 0;
  padding: 3px;
  cursor: pointer;
}

.conversation-appearance__controls input[type='text'] {
  flex: 1;
  min-width: 0;
  width: 100%;
  padding: 0 8px;
  font: 0.85rem monospace;
}

.conversation-appearance__reset {
  border: 0;
  background: transparent;
  color: var(--theme-on-surface);
  padding: 8px 2px;
  font-size: 0.78rem;
  cursor: pointer;
}

.conversation-appearance__reset:disabled {
  opacity: 0.4;
  cursor: default;
}

.conversation-appearance__preview {
  margin-top: 18px;
  padding: 14px;
  border: 1px solid var(--theme-border-hover);
  border-radius: 8px;
  background: var(--theme-surface-solid);
}

.conversation-appearance__preview .story-prose {
  margin-top: 10px;
  font: 1rem/1.5 var(--story-font-family, Georgia, serif);
}

.conversation-appearance__reset-all {
  display: block;
  margin-top: 10px;
}
.appearance-actions { display: flex; gap: 12px; margin-top: 18px; }
</style>
